/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useState } from 'react'
import { Form as FinalForm, FormRenderProps } from 'react-final-form'
import { FieldArray } from 'react-final-form-arrays'
import arrayMutators from 'final-form-arrays'
import createDecorator from 'final-form-focus'
import { sortBy } from 'lodash'
import Typography from '@mui/material/Typography'
import { useParams } from 'react-router-dom'
import { InputField } from 'components/Input'
import QuestionPicker from 'components/QuestionPicker'
import { Revision, Revisions } from 'components/Revisions'
import DetailsBlock from 'components/DetailsBlock'
import QuestionAccordion from 'components/QuestionAccordion'
import TabsNavigation from 'components/TabsNavigation'
import { CourseName } from 'theme/utils'
import { validateRequired } from 'utils/validation'
import {
  CreateQuestionBankInput,
  MembershipType,
  QuestionInvertTypeEnum,
  QuestionLocationTypeEnum,
} from '__generatedTypes__/globalTypes'
import { findOneQuestionBank_findOneQuestionBank as QB } from '__generatedTypes__/findOneQuestionBank'
import { findAllQuestion_findAllQuestion_items as Question } from '__generatedTypes__/findAllQuestion'
import { useQuestionsSubmitError } from 'hooks/useQuestionsSubmitError'
import { MembershipLevelSelect } from 'components/MembershipLevelSelect'
import { prepareEditorHtml } from 'components/Editor'
import { toast } from 'utils/toast'
import { PlatformSlug } from 'constants/consts'
import { PRACTICE_TEST_MEDIA_PATH_S3 } from 'constants/images'
import { TabsItem } from 'types/tabsItem'
import { Passage } from 'pagesNew/PracticeTests/Form/PassagesFieldArray'
import { isGREPassagesAvailable } from 'pagesNew/Questions/Form/questionTypeConfig'
import * as commonClasses from '../../Video/commonStyles'
import { getAvailableQuestionTypes, getQBTitle, isCaseScenarioSubject } from '../utils'
import { GREPassagesFieldArray } from './GREPassagesFieldArray'
import { validatePassages } from './validatePassages'
import * as classes from './styles'

export type QuestionBank = QB & {
  questions: Question[]
  membershipLevel?: MembershipType
}

export type FormState = Omit<QuestionBank, 'passage'> & {
  passage?: Passage[]
}

export type Props = {
  questionBankOrCategoryId: QuestionBank | string
  subject: QuestionBank['questionBankCategory']['subject']
  subjectCategories?: Array<{ id: string; title: string; membershipLevel: MembershipType; tabIndex: number }>
  showMembershipLevelSelect?: boolean
  onSubmit: (questionBank: CreateQuestionBankInput) => Promise<void>
  tabIndex: number
  onDelete?: () => Promise<void>
  defaultOrder?: number
}

type TabsItemType = TabsItem & {
  fields: Array<keyof FormState>
}

const BUILDER_TAB: TabsItemType = {
  id: 'builder',
  title: 'Builder',
  fields: ['title', 'membershipLevel', 'questions'],
}

const PASSAGES_TAB: TabsItemType = {
  id: 'passage',
  title: 'Passages',
  fields: ['passage'],
}

const TABS: TabsItemType[] = [BUILDER_TAB, PASSAGES_TAB]

const isHasError = (
  fields: Array<keyof FormState>,
  errors: FormRenderProps['errors'],
  touched: FormRenderProps['touched']
) => fields.some((field) => touched?.[field] && errors?.[field])

const focusOnErrors = createDecorator<FormState, Partial<FormState>>()

export const prepareForSubmit = async (
  values: Partial<FormState>,
  includePassages = false
): Promise<CreateQuestionBankInput> => {
  const formattedPassages = includePassages
    ? await Promise.all(
        values.passage?.map(async (passage) => {
          const content = await prepareEditorHtml(passage.content ?? '', PRACTICE_TEST_MEDIA_PATH_S3)

          return {
            id: passage.id,
            title: passage.title ?? '',
            startQuestion: Number(passage.startQuestion),
            endQuestion: Number(passage.endQuestion),
            content,
            category: null,
            subCategory: null,
            reference: passage.reference ?? null,
            watchPassageWalkthrough: passage.watchPassageWalkthrough ?? null,
            invertContentImages: passage.invertContentImages ?? QuestionInvertTypeEnum.AUTO,
          }
        }) ?? []
      )
    : []

  return {
    history: values.history,
    isLive: values.isLive,
    order: values.order,
    questionBankCategory: values.questionBankCategory?.id ? { id: values.questionBankCategory.id } : undefined,
    questionIds: values.questions?.map(({ id }) => id),
    questions: values.questions?.map(({ id }) => ({ id })),
    title: values.title,
    passage: formattedPassages,
  }
}

export const getInitialValues = (
  questionBankOrCategoryId: QuestionBank | string,
  defaultOrder = 0
): Partial<FormState> => {
  if (typeof questionBankOrCategoryId === 'string') {
    return {
      history: [],
      isLive: false,
      order: defaultOrder,
      questionBankCategory: { id: questionBankOrCategoryId } as QuestionBank['questionBankCategory'],
      questionIds: [],
      questions: [],
      passage: [],
      title: '',
    }
  }

  return {
    ...questionBankOrCategoryId,
    questions: (questionBankOrCategoryId.questionIds
      .map((questionId) => questionBankOrCategoryId.questions.find(({ id }) => id === questionId))
      .filter((question) => !!question) ?? []) as QuestionBank['questions'],
    membershipLevel: questionBankOrCategoryId.questionBankCategory.membershipLevel,
    passage: questionBankOrCategoryId.passage?.length
      ? sortBy(questionBankOrCategoryId.passage, 'startQuestion').map((passage) => ({
          id: passage.id,
          title: passage.title,
          startQuestion: passage.startQuestion,
          endQuestion: passage.endQuestion,
          content: passage.content,
          invertContentImages: passage.invertContentImages ?? QuestionInvertTypeEnum.AUTO,
          reference: passage.reference,
          watchPassageWalkthrough: passage.watchPassageWalkthrough,
        }))
      : [],
  }
}

export const Form = ({
  questionBankOrCategoryId,
  subject,
  subjectCategories = [],
  showMembershipLevelSelect = false,
  tabIndex,
  onSubmit,
  onDelete,
  defaultOrder = 0,
}: Props) => {
  const [submitting, setSubmitting] = useState(false)
  const [activeTab, setActiveTab] = useState<TabsItem>(BUILDER_TAB)
  const [initialValues, setInitialValues] = useState(getInitialValues(questionBankOrCategoryId, defaultOrder))
  const { submitQuestionError, setSubmitQuestionError, handleServerError } = useQuestionsSubmitError()
  const isCaseBasedScenario = isCaseScenarioSubject(subject)

  const { platform: platformSlug } = useParams()

  useEffect(() => {
    setInitialValues(getInitialValues(questionBankOrCategoryId, defaultOrder))
    // TODO: need to figure out why without "?.id" useEffect runs and resets initialValues
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
  }, [questionBankOrCategoryId?.id])

  const handleSubmit = useCallback(
    async (values: FormState) => {
      try {
        const questionBankData = await prepareForSubmit(values, isGREPassagesAvailable(platformSlug))

        if (showMembershipLevelSelect && values.membershipLevel) {
          const selectedCategoryId = subjectCategories?.find(
            ({ membershipLevel, tabIndex: categoryTabIndex }) =>
              membershipLevel === values.membershipLevel && tabIndex === categoryTabIndex
          )?.id

          if (selectedCategoryId) {
            questionBankData.questionBankCategory = {
              id: selectedCategoryId,
            }
          }
        }

        await onSubmit(questionBankData)
        setSubmitQuestionError(null)
      } catch (e) {
        toast.error('Failed to prepare data for submit')
        console.error(e)
        handleServerError(e)
      }
    },
    [onSubmit, tabIndex, showMembershipLevelSelect, subjectCategories, platformSlug]
  )

  const handleDelete = useCallback(async () => {
    setSubmitting(true)
    try {
      await onDelete?.()
    } catch (e) {
      console.error(e)
    }
    setSubmitting(false)
  }, [onDelete])

  const validate = useCallback(
    (values: FormState) => {
      if (!isGREPassagesAvailable(platformSlug)) {
        return undefined
      }

      const passage = validatePassages(values.passage, values.questions)
      return passage ? { passage } : undefined
    },
    [platformSlug]
  )

  const entityTitle = getQBTitle(isCaseBasedScenario, tabIndex, false)

  const renderBuilderFields = (form: FormRenderProps<FormState>['form'], values?: FormState) => (
    <>
      <InputField
        name="title"
        validate={validateRequired}
        label="Title"
        placeholder="Enter Title"
        fullWidth
        data-testid="title-input"
      />
      {showMembershipLevelSelect && (
        <MembershipLevelSelect name="membershipLevel" validate={validateRequired} fullWidth />
      )}
      <FieldArray<Question> name="questions" validate={validateRequired}>
        {({ fields, meta }) => (
          <>
            <QuestionPicker
              label="Questions"
              value={fields.value ?? []}
              onChange={(questions) => form.change('questions', questions)}
              error={meta.touched && meta.error}
              formHelperText={meta.touched && typeof meta.error === 'string' ? meta.error : undefined}
              filter={{
                platform: { id: String(subject?.platform?.id) },
                types: getAvailableQuestionTypes({ tabIndex, platformSlug: platformSlug as PlatformSlug }),
              }}
              willUsedIn={QuestionLocationTypeEnum.QB}
              hideSelectedItems
              fullWidth
            />
            <QuestionAccordion
              questions={fields.value ?? []}
              onSort={(questions) => form.change('questions', questions)}
              onDeleteClick={(_, index) => fields.remove(index)}
              onQuestionUpdated={(questionUpd, index) => fields.update(index, questionUpd)}
              passages={isGREPassagesAvailable(platformSlug) ? values?.passage ?? [] : undefined}
            />
          </>
        )}
      </FieldArray>
      {!!initialValues.history?.length && <Revisions revisions={(initialValues?.history ?? []) as Revision[]} />}
    </>
  )

  return (
    <div css={commonClasses.container} data-testid="qb-form-wrapper">
      <Typography variant="title3" css={commonClasses.title} data-testid="qb-title">
        {initialValues.id ? 'Edit' : 'Create'} {entityTitle}
      </Typography>
      <FinalForm<FormState>
        mutators={{ ...arrayMutators }}
        decorators={[focusOnErrors]}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validate={validate}
        render={({ handleSubmit, initialValues, submitting: formSubmitting, form, values, errors, touched }) => (
          <form onSubmit={handleSubmit} css={commonClasses.contentContainer} data-testid="qb-form">
            <div css={commonClasses.block}>
              {isGREPassagesAvailable(platformSlug) ? (
                <>
                  <TabsNavigation
                    tabs={TABS.map((tab) => ({
                      ...tab,
                      error: isHasError(tab.fields, errors, touched),
                    }))}
                    value={activeTab}
                    onChange={setActiveTab}
                  />
                  <div css={classes.tabContent(activeTab.id === BUILDER_TAB.id)}>
                    {renderBuilderFields(form, values)}
                  </div>
                  <div css={classes.tabContent(activeTab.id === PASSAGES_TAB.id)}>
                    <FieldArray name="passage">
                      {({ fields, meta }) => <GREPassagesFieldArray fields={fields} meta={meta} />}
                    </FieldArray>
                  </div>
                </>
              ) : (
                renderBuilderFields(form)
              )}
            </div>
            <DetailsBlock
              title="Test Details"
              itemName={entityTitle}
              tag={(subject?.tabName ?? 'Unknown') as CourseName}
              tagLabel={
                isCaseBasedScenario || entityTitle !== 'Question Bank'
                  ? entityTitle
                  : tabIndex > 1
                  ? `${subject?.tabName} ${tabIndex}`
                  : ''
              }
              createDate={initialValues?.createdAt}
              statusLive={!!initialValues?.isLive}
              onDeleteClick={handleDelete}
              disabled={formSubmitting || submitting}
              deleteDisabledReason={
                initialValues.isLive ? `You can't delete ${entityTitle} because Live is enabled` : undefined
              }
              errorText={submitQuestionError}
            />
          </form>
        )}
      />
    </div>
  )
}
